import { SearchCriteria } from "../../../pojo/SearchCriteria";
import { Item } from "../../../pojo/Item";
import { DataAccess } from "../../../dataaccess/data.access";
import { S25Util } from "../../../util/s25-util";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { CustomAttributes } from "../../../pojo/CustomAttributes";
import { Proto } from "../../../pojo/Proto";
import Model = SearchCriteria.Model;

/**
 * Document object for Student Engagement organization search results.
 * Contains core organization fields plus SE-specific custom attributes.
 */
export type SEOrganizationDocument = {
    itemId: number;
    itemName: string;
    itemTypeId: number;
    txt: string;
    title?: string;
    logo?: string; //holds the URL from the server
    logoUrl?: Proto.URLString; //property name used everywhere else //TODO separate search results type from server format
    mission?: string;
    lastModified?: string;
};

/**
 * Response wrapper for SE organization search with pagination
 */
export type SEOrganizationSearchResponse = Proto.Paginate & {
    organizations: SEOrganizationDocument[];
};

/**
 * Document object for Student Engagement event search results.
 * Contains core event fields plus SE-specific custom attributes.
 */
export interface SEEventDocument {
    itemId: number;
    itemName: string;
    itemTypeId: number;
    txt: string;
    title?: string;
    imageUrl?: string;
    startDate?: string;
    endDate?: string;
    location?: string;
    locations?: string[];
    lastModified?: string;
}

/**
 * Response wrapper for SE event search with pagination
 */
export type SEEventSearchResponse = Proto.Paginate & {
    events: SEEventDocument[];
};

/**
 * Service for Student Engagement search functionality.
 * Handles searching for SE organizations and events with appropriate filters.
 */
export class SeSearchService {
    /**
     * Search for Student Engagement organizations with pagination.
     * Uses the dedicated SE organizations endpoint that returns enriched data
     * including logo, title, and mission.
     *
     * @param searchTerm - Search term for organization name (optional)
     * @param page - Page number, 1-based (optional, default 1)
     * @param pageSize - Number of results per page (optional, default 20)
     * @param paginateKey - Pagination key for fetching subsequent pages (optional, default "T")
     * @returns Promise resolving to search response with organizations and pagination metadata
     */
    public static async searchOrganizations(
        searchTerm: string,
        page: number = 1,
        pageSize: number = 20,
        paginateKey: string = "T",
    ): Promise<SEOrganizationSearchResponse> {
        try {
            // Build query string
            const params = new URLSearchParams();
            if (searchTerm && searchTerm.trim()) {
                params.append("name", searchTerm.trim());
            }
            params.append("paginate", paginateKey);
            params.append("page", page.toString());
            params.append("page_size", pageSize.toString());

            const queryString = params.toString();

            // Call SE organizations endpoint
            const response = await DataAccess.get<SEOrganizationSearchResponse>(
                DataAccess.injectCaller(
                    `/tile/organizations.json?${queryString}`,
                    "SeSearchService.searchOrganizations",
                ),
            );

            S25Util.array.forceArray(response.organizations)?.forEach((org) => {
                org.logoUrl = org.logo;
            });

            return response;
        } catch (error) {
            console.error("Error searching SE organizations:", error);
            throw error;
        }
    }

    /**
     * Search for Student Engagement events with pagination.
     * Uses the dedicated SE events endpoint that returns enriched data
     * including image, title, dates, and locations.
     *
     * @param searchTerm - Search term for event name (optional)
     * @param page - Page number, 1-based (optional, default 1)
     * @param pageSize - Number of results per page (optional, default 20)
     * @returns Promise resolving to search response with events and pagination metadata
     */
    public static async searchEvents(
        searchTerm: string,
        page: number = 1,
        pageSize: number = 20,
        paginateKey: string = "T",
    ): Promise<SEEventSearchResponse> {
        try {
            // Build query string
            const params = new URLSearchParams();
            if (searchTerm && searchTerm.trim()) {
                params.append("name", searchTerm.trim());
            }
            params.append("paginate", paginateKey);
            params.append("page", page.toString());
            params.append("page_size", pageSize.toString());
            params.append("node_type", "E");
            //Events this week
            params.append(
                "starts_after",
                S25Util.date.toS25ISODateStrStartOfDay(
                    S25Util.date.firstDayOfWeekFromDate(new Date(), S25Util.date.dayOfWeekNumber("Sunday")),
                ),
            );

            const queryString = params.toString();

            // Call SE events endpoint
            return DataAccess.get<SEEventSearchResponse>(
                DataAccess.injectCaller(`/tile/events.json?${queryString}`, "SeSearchService.searchEvents"),
            );
        } catch (error) {
            console.error("Error searching SE events:", error);
            throw error;
        }
    }
}
